import { Alert } from '@designsforhealth/dfh-react-components'
import { GraphQLError } from 'graphql'
import { compose, test } from 'ramda'
import React, { useMemo, useState } from 'react'

import { useAutoShipOrder } from '../../../../hooks/auto_ships/useAutoShipOrder'
import { useAutoShipResume } from '../../../../hooks/auto_ships/useAutoShipResume'
import { useAutoShipSuspend } from '../../../../hooks/auto_ships/useAutoShipSuspend'
import { addDispensaryBasePathToPath, useDispensaryContext } from '../../../../lib/dispensaries'
import { usePageUrl } from '../../../../lib/pages'
import { daysFromToday, monthDayYear } from '../../../../utils/dateTools'
import { formatAsCurrency } from '../../../../utils/priceTools'
import { pluralize } from '../../../../utils/textTools'
import Spinner from '../../../Spinner'
import AutoShipCancelResumeModal from './AutoShipCancelResumeModal'
import AutoShipItem from './AutoShipItem'
import AutoShipNotice from './AutoShipNotice'
import {
  AutoShipBottomRow,
  AutoShipButton,
  AutoShipItemCostAndFrequency,
  AutoShipItemCount,
  AutoShipItemRow,
  AutoShipMiddleRow,
  AutoShipNextShipment,
  AutoShipOrderLink,
  AutoShipOrderNumber,
  AutoShipSeparator,
  AutoShipStatusTag,
  AutoShipStatusWrapper,
  AutoShipTopRow,
  ShowAllButtonLink,
} from './styled'
import type { AutoShipProps } from './types'

const isProductNotAvailableError = compose(
  test(/Product that you are trying to add is not available/),
  (obj: GraphQLError) => obj.message,
)

const AutoShipStatus: React.VFC<AutoShipProps> = ({
  autoShip,
  itemsAndProducts,
  productsLoading,
  total,
}) => {
  const [showMore, setShowMore] = useState(false)
  const toggleShowMore = () => {
    setShowMore((prevShowLink) => !prevShowLink)
  }

  const [isCancelResumeModalOpen, setIsCancelResumeModalOpen] = useState(false)
  const toggleCancelResumeModal = () => {
    setIsCancelResumeModalOpen(!isCancelResumeModalOpen)
  }

  const daysToReactivate = useMemo(
    () => daysFromToday(autoShip.nextShipDate) || -1,
    [autoShip.nextShipDate],
  )
  const itemsAndProductsShown = useMemo(
    () => (showMore ? itemsAndProducts : itemsAndProducts.slice(0, 3)),
    [itemsAndProducts, showMore],
  )

  // Restart a completed/cancelled auto-ship's items
  const { addToCart, ordering, orderError } = useAutoShipOrder(autoShip)
  const { navigate } = usePageUrl()
  const dispensary = useDispensaryContext()
  const restartAutoShip = () => {
    if (!addToCart) {
      return
    }
    addToCart().then(() =>
      navigate(
        dispensary
          ? addDispensaryBasePathToPath(dispensary.slug, '/checkout/shipping')
          : '/checkout/shipping',
      ),
    )
  }

  // Resume a cancelled auto-ship
  const { resumeAutoShip, resuming, resumeError } = useAutoShipResume(autoShip)
  const resumeSuspendedAutoShip = () => {
    if (!resumeAutoShip) {
      return
    }
    setIsCancelResumeModalOpen(false)
    resumeAutoShip()
  }

  // Suspend an active auto-ship
  const { suspendAutoShip, suspending, suspendError } = useAutoShipSuspend(autoShip)
  const suspendActiveAutoShip = () => {
    if (!suspendAutoShip) {
      return
    }
    setIsCancelResumeModalOpen(false)
    suspendAutoShip()
  }

  const errorMessage = useMemo(() => {
    if (!orderError && !resumeError && !suspendError) {
      return
    }
    if (orderError) {
      const stockError = orderError.graphQLErrors.find(isProductNotAvailableError)
      if (stockError) {
        return stockError.message
      }
    }
    return `An error occurred ${
      orderError
        ? 'restarting'
        : resumeError
          ? 'reactivating'
          : suspendError
            ? 'cancelling'
            : 'with'
    } this Auto-Ship. Please try again and contact Customer Experience if error persists.`
  }, [orderError, resumeError, suspendError])

  return (
    <AutoShipStatusWrapper>
      {errorMessage && <Alert type="error">{errorMessage}</Alert>}

      <AutoShipTopRow>
        <AutoShipOrderNumber>Auto-Ship #{autoShip.autoShipNumber}</AutoShipOrderNumber>
        <AutoShipOrderLink to={`/account/orders/${autoShip.originalOrderNumber}`}>
          Order: #<AutoShipOrderNumber>{autoShip.originalOrderNumber}</AutoShipOrderNumber>
        </AutoShipOrderLink>
      </AutoShipTopRow>

      <AutoShipMiddleRow>
        <AutoShipItemCount>
          {itemsAndProducts.length}{' '}
          {pluralize({
            count: itemsAndProducts.length,
            word: 'item',
          })}
        </AutoShipItemCount>
      </AutoShipMiddleRow>

      <AutoShipMiddleRow>
        <AutoShipItemCostAndFrequency>
          {!productsLoading && formatAsCurrency({ amount: total })} {autoShip.frequency}
        </AutoShipItemCostAndFrequency>
        {autoShip.status !== 'Active' && (
          <AutoShipStatusTag status={autoShip.status}>
            {autoShip.status.includes('Payment Failed')
              ? 'Payment Failed'
              : autoShip.status === 'Suspended' && daysToReactivate > 0
                ? 'Paused'
                : autoShip.status === 'Suspended' && daysToReactivate <= 0
                  ? 'Expired'
                  : autoShip.status}
          </AutoShipStatusTag>
        )}
      </AutoShipMiddleRow>

      {autoShip.status !== 'Active' && (
        <AutoShipNotice
          status={autoShip.status}
          daysToReactivate={daysToReactivate}
          isOrdering={ordering}
          isResuming={resuming}
          toggleCancelResumeModal={toggleCancelResumeModal}
          restartAutoShip={restartAutoShip}
        />
      )}

      {autoShip.status === 'Active' && (
        <AutoShipBottomRow>
          <AutoShipNextShipment>
            Next Shipment will be on <strong>{monthDayYear(autoShip.nextShipDate)}</strong>
          </AutoShipNextShipment>
          <AutoShipButton
            text={suspending ? 'Pausing...' : 'Pause'}
            disabled={suspending}
            onClick={suspending ? undefined : toggleCancelResumeModal}
          />
        </AutoShipBottomRow>
      )}

      <AutoShipBottomRow>
        <AutoShipSeparator />
      </AutoShipBottomRow>

      <Spinner loading={productsLoading} />

      <AutoShipItemRow>
        {itemsAndProductsShown.map(([item, product]) => (
          <AutoShipItem item={item} key={item.sku} product={product} />
        ))}
      </AutoShipItemRow>

      {itemsAndProductsShown.length < itemsAndProducts.length && (
        <ShowAllButtonLink onClick={toggleShowMore} underline>
          {showMore ? 'Show Less' : 'Show More'}
        </ShowAllButtonLink>
      )}
      <AutoShipCancelResumeModal
        isOpen={isCancelResumeModalOpen}
        toggleModal={toggleCancelResumeModal}
        status={autoShip.status === 'Active' ? 'SUSPENDED' : 'ACTIVE'}
        confirm={autoShip.status === 'Active' ? suspendActiveAutoShip : resumeSuspendedAutoShip}
        itemsAndProducts={itemsAndProducts}
        autoShipNumber={autoShip.autoShipNumber}
      />
    </AutoShipStatusWrapper>
  )
}

export default AutoShipStatus
